import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import LoadSpinner from "../../../utilities/LoadSpinner";
import ReadOnlyField from "./FormFieldReadOnly";
import { buildFieldsById, resolvePiping } from "./formLogic";
import { getRightworkAPI } from "../requests/utilities/requests";
import { API_get_learning_form_detail } from "../../../constants";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 640,
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  overflowY: "auto",
  border: "none",
};

// `previewForm` previews an in-progress draft directly (from the editor) with no
// fetch; otherwise `formUuid` fetches the saved form (from the library card).
function FormPreviewModal({ open, onClose, formUuid, homeCompany, previewForm }) {
  const [fetchedForm, setFetchedForm] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    // Skip the fetch when a draft was handed in directly.
    if (!open || previewForm || !formUuid) return;
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setFetchedForm(null);
      try {
        const res = await getRightworkAPI(API_get_learning_form_detail, {
          company_id: homeCompany,
          learning_form_uuid: formUuid,
        });
        if (!cancelled) {
          setFetchedForm(res.data?.redux?.payload?.learning_form || null);
        }
      } catch (e) {
        // Leave it null -> render the unavailable state.
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, formUuid, homeCompany, previewForm]);

  const form = previewForm || fetchedForm;
  const isFetching = !previewForm && isLoading;
  const fields = form?.fields || [];
  const fieldsById = buildFieldsById(fields);

  return (
    // disableEnforceFocus: this can open on top of the editor modal, and two
    // MUI focus traps would otherwise fight over focus.
    <Modal open={open} onClose={onClose} disableEnforceFocus>
      <Box sx={style}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10 rounded-t-2xl">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-xl font-bold text-slate-900 truncate">
                {form?.name || "Form preview"}
              </h3>
            </div>
            <button
              className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 shrink-0"
              onClick={onClose}
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        {isFetching ? (
          <div className="flex items-center justify-center h-64">
            <LoadSpinner />
          </div>
        ) : !form ? (
          <div className="px-6 py-16 text-center text-sm text-slate-400">
            This form is currently unavailable.
          </div>
        ) : (
          <div className="px-6 py-5">
            {form.description && (
              <p className="text-sm text-slate-600 mb-2">{form.description}</p>
            )}

            {/* Meta badges */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                {form.completed_by_role === "manager"
                  ? "Manager-completed"
                  : "Learner-completed"}
              </span>
              {form.scoring_enabled && (
                <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700">
                  Scored
                  {form.pass_threshold !== null &&
                  form.pass_threshold !== undefined
                    ? ` • Pass ≥ ${form.pass_threshold}`
                    : ""}
                  {form.gate_on_pass ? " • Must pass to continue" : ""}
                </span>
              )}
            </div>

            {fields.length === 0 ? (
              <p className="text-sm text-slate-400">
                This form has no fields yet.
              </p>
            ) : (
              <div className="space-y-4">
                {fields.map((f) => (
                  <ReadOnlyField
                    key={f.id}
                    field={{
                      ...f,
                      // Preview has no answers: show the referenced question in
                      // brackets where a piped answer will go.
                      label: resolvePiping(f.label, {}, fieldsById, (id, ref) =>
                        `[${ref?.label || "answer"}]`
                      ),
                      help: resolvePiping(f.help, {}, fieldsById, (id, ref) =>
                        `[${ref?.label || "answer"}]`
                      ),
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </Box>
    </Modal>
  );
}

export default FormPreviewModal;
